import { Injectable, inject } from '@angular/core';
import { map, Observable, merge, withLatestFrom, of, EMPTY, mergeMap, distinctUntilChanged, BehaviorSubject, tap } from 'rxjs';
import { ProjectStore } from '../../state/project/project.store';
import { HeatloadStore } from '../../state/heatload/heatload.store';
import { BuildingType, FormName } from '@nx-customer-apps/shared/enums';
import { Question } from '@nx-customer-apps/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { HeatGeneratorDetailsFormValue } from '@nx-customer-apps/shared/interfaces';
import { HeatloadFormOptionsService } from './heatload-form-options.service';
import { HeatloadPrefillMapper } from '../../services';
import { AccordionService } from '../../shared/interfaces';
import { HeatGeneratorDetailsFormService } from '../../shared/components/heat-generator-details';

@Injectable({
    providedIn: 'root'
})
export class HeatGeneratorDetailsService implements AccordionService {
    private prefillMapper = inject(HeatloadPrefillMapper);
    private heatloadStore = inject(HeatloadStore);
    private heatloadFormOptionsService = inject(HeatloadFormOptionsService);
    private projectStore = inject(ProjectStore);
    private formService = inject(HeatGeneratorDetailsFormService);
    private translate = inject(TranslateService);

    constructor() {
        this.initBuildingConstructionYear();
    }

    public questions$: Observable<Question[] | null> = merge(this.onEnergySource$(), this.onHeatingSystems$()).pipe(distinctUntilChanged());
    public isExpanded$ = this.heatloadStore.sameValueSubmited$.pipe(map(form => form?.formName === FormName.EnergySource));
    /**
     * Sometimes it is possible only to prefill just the installation year. It is needed to send an event that the prefilling is finished even though it was not a full prefill.
     */
    private isConstructionYearPrefillFinished$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private buildingConstructionYear$: BehaviorSubject<number | undefined>;

    public getSuccessMessage(options: HeatGeneratorDetailsFormValue): string {
        const unit = this.translate.instant('MEASUREMENT_UNITS.KW');
        const installation = this.translate.instant(`HEATLOAD.HEAT_GENERATOR_DETAILS.BOILER_TYPE.${options.boilerType}`);
        return `${installation}, ${options.installationYear}, ${options.nominalPower} ${unit}`;
    }

    /**
     * Indicates a moment when a prefill without form value emition is needed.
     * Usually it changes its value many times. Whenever the form has to be filled again
     * and there is a prefill source available (project data, or store cache data)
     */
    public isSimplePrefillNeeded(): boolean {
        return !this.isConstructionYearPrefillFinished$.value;
    }

    /**
     * Here we return a clean UNPREFILLED question set. The question set for just component initialization.
     */
    private onEnergySource$() {
        return this.heatloadStore.energySource$.pipe(
            withLatestFrom(this.projectStore.project$, this.heatloadStore.cache$),
            map(([energySource, project, cache]) => ({
                energySource,
                project,
                cache
            })),
            mergeMap(state => {
                if (!state.energySource) {
                    return of(null);
                }

                const cacheInstallationYear = state.cache?.heatGeneratorDetails?.installationYear;
                const installationConstructionYear = state.project!.building.currentHeatingSystem?.installationConstructionYear;
                const installationYearForHeatingSystems =
                    cacheInstallationYear || installationConstructionYear || this.buildingConstructionYear$.value;

                if (this.isSimplePrefillNeeded() && installationYearForHeatingSystems) {
                    this.heatloadStore.getHeatingSystems(`${installationYearForHeatingSystems}`);
                    return EMPTY;
                }

                const options = this.heatloadFormOptionsService.getHeatGeneratorDetailsFormOptions({
                    buildingType: state.project!.building.buildingType as BuildingType
                });

                const questions = this.formService.getHeatGeneratorDetailsQuestions(options);
                return of(questions);
            })
        );
    }

    /**
     * Here we return a PREFILLED question set. The question set for just component initialization.
     */
    private onHeatingSystems$() {
        return this.heatloadStore.heatingSystems$.pipe(
            withLatestFrom(this.projectStore.project$, this.heatloadStore.energySource$, this.heatloadStore.cache$),
            map(([heatingSystems, project, energySourceState, cache]) => ({
                heatingSystems,
                project,
                energySourceState,
                cache
            })),
            mergeMap(state => {
                if (!this.isSimplePrefillNeeded()) {
                    return EMPTY;
                }

                if (!state.heatingSystems) {
                    return of(null);
                }

                const projectPrefill = this.prefillMapper.projectToHeatGeneratorDetailsPrefill(state.project);
                const cachePrefill = this.prefillMapper.stateToHeatGeneratorDetailsPrefill(state.cache?.heatGeneratorDetails);
                const options = this.heatloadFormOptionsService.getHeatGeneratorDetailsFormOptions({
                    buildingType: state.project!.building.buildingType as BuildingType,
                    prefill: cachePrefill || projectPrefill,
                    energySourceState: state.energySourceState,
                    heatingSystems: state.heatingSystems,
                    buildingConstructionYear: this.buildingConstructionYear$.value
                });

                const questions = this.formService.getHeatGeneratorDetailsQuestions(options);

                return of(questions);
            })
        );
    }

    public onPrefillInit(): void {
        this.toggleIsConstructionYearPrefillFinished(true);
    }

    public onSimplePrefillInit(): void {
        this.toggleIsConstructionYearPrefillFinished(true);
    }

    public toggleIsConstructionYearPrefillFinished(value: boolean): void {
        this.isConstructionYearPrefillFinished$.next(value);
    }

    private initBuildingConstructionYear(): void {
        this.projectStore.project$
            .pipe(tap(project => (this.buildingConstructionYear$ = new BehaviorSubject(project?.building.buildingConstructionYear))))
            .subscribe();
    }
}
